import { useEffect, useRef } from "react";

import { Error as ErrorIcon } from "@mui/icons-material";

import { findBrandByDomain } from "../../configs/configLoader";
import { font, welcomeScreenUI } from "../../configs";

import { VALIDATION_RULES } from "../../constants/modal";
import {
  getInputStyles,
  handleMoveToNextInput,
  isPhysicalAndroidDevice,
  useIsMobile,
} from "../../utils";
import { BRANDS_WITH_NEW_INPUTS } from "../../constants/inputs";

import "./index.css";

interface TextFieldComponentProps {
  id: string;
  value?: string;
  unit: string;
  unitWeight: string;
  placeholder: string;
  handleChange: (e: any) => void;
  handleClick?: () => void;
  setBlurSignal: React.Dispatch<React.SetStateAction<string>>;
  setNeedValidate: React.Dispatch<React.SetStateAction<boolean>>;
  error: boolean;
  errorType: string;
  index: number;
  inputRefs: React.RefObject<HTMLInputElement>[];
  feetValue?: string;
  disableContinue: boolean;
  nextStep: () => void;
  unitAge: string;
  setTouchedCompany: React.Dispatch<React.SetStateAction<boolean>>;
  setFeetTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setInchesTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

const TextFieldComponent: React.FC<TextFieldComponentProps> = ({
  id,
  value,
  unit,
  unitWeight,
  placeholder,
  handleChange,
  handleClick,
  setBlurSignal,
  setNeedValidate,
  error,
  errorType,
  index,
  inputRefs,
  feetValue,
  disableContinue,
  nextStep,
  unitAge,
  setTouchedCompany,
  setFeetTouched,
  setInchesTouched,
}) => {
  const isMobile = useIsMobile();

  const brandDefined = findBrandByDomain();

  const timeoutRef = useRef<number | null>(null);

  const isAndroid = isPhysicalAndroidDevice();

  const inputStyleSelector = welcomeScreenUI.input_fields.value;

  const isNewStyledInputs =
    brandDefined && BRANDS_WITH_NEW_INPUTS.includes(brandDefined.name);

  const urlParameters = new URLSearchParams(window.location.search);
  const isChildren =
    urlParameters.get("category") === "children" ||
    urlParameters.get("kids") === "true";

  const isLacoste = brandDefined?.name === "Lacoste";
  const isKenzo = brandDefined?.name === "KENZO";
  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isVBMobile = isVB && isMobile;
  const isGivenchyMobile = brandDefined?.name === "Givenchy" && isMobile;

  const borderColor = error ? "#DA3939" : inputStyleSelector.empty.fontColor;

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const inputStyles = {
    "--input-bg": inputStyleSelector.empty.backgroundColor,
    "--input-text": !value
      ? inputStyleSelector.empty.fontColor
      : error && errorType === id && !isIntersport
        ? "#DA3939"
        : inputStyleSelector.filled.fontColor,
    backgroundColor: "var(--input-bg)",
    color: "var(--input-text)",
    fontSize: isIOS ? "16px" : inputStyleSelector.empty.fontSize,
    marginBottom:
      inputStyleSelector.marginBottom as React.CSSProperties["marginBottom"],
    padding: isGivenchyMobile
      ? "12px 20px 12px 10px"
      : isVBMobile
        ? "6.5px 15px"
        : isMobile
          ? "12px 20px"
          : (inputStyleSelector.padding as React.CSSProperties["padding"]),
    fontWeight: (!value
      ? inputStyleSelector.empty.fontWeight
      : inputStyleSelector.filled
          .fontWeight) as React.CSSProperties["fontWeight"],
    letterSpacing: (!value
      ? inputStyleSelector.empty
      : (inputStyleSelector.filled as any)
    )?.letterSpacing,
    // border styles
    borderStyle: isKenzo ? "none" : "solid",
    borderWidth: isBlackstore
      ? "2px"
      : isNewStyledInputs
        ? "1px"
        : isKenzo
          ? "0px"
          : "0px",
    borderRadius: isMobile
      ? inputStyleSelector?.mobileBorderRadius
      : inputStyleSelector.borderRadius,
    borderTopColor:
      error && errorType === id ? "#DA3939" : inputStyleSelector.borderColor,
    borderLeftColor:
      error && errorType === id ? "#DA3939" : inputStyleSelector.borderColor,
    borderRightColor:
      error && errorType === id ? "#DA3939" : inputStyleSelector.borderColor,
    borderBottomWidth: inputStyleSelector.borderBottom ? "1px" : "0px",
    borderBottomColor:
      error && errorType === id ? "#DA3939" : inputStyleSelector.borderColor,
  };

  const handleFocus = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    if (isMobile) {
      setBlurSignal(error ? id : "");
      setNeedValidate(true);

      if (!isLacoste)
        e.target.scrollIntoView({ behavior: "smooth", block: "center" });
      const popup = document.querySelector(".drawer-popup") as HTMLElement;
      if (popup) {
        popup.style.position = "fixed";
        popup.style.zIndex = "999";
        popup.style.bottom = "0";
      }
    }
  };

  const handleBlur = (e: any) => {
    e.stopPropagation();

    if (id === "feet") setFeetTouched(true);
    if (id === "inches") setInchesTouched(true);

    setBlurSignal(error ? id : "");
    setNeedValidate(true);

    if (id === "company") {
      setTouchedCompany(true);
    }

    if (isMobile) {
      const popup = document.querySelector(".drawer-popup") as HTMLElement;
      if (popup) {
        popup.style.bottom = "0";
      }
    }

    if (isIOS) {
      setTimeout(() => {
        window.scrollTo(window.scrollX, window.scrollY);
      }, 0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    const inputValue = e.target.value;

    if (error && !inputValue) setNeedValidate(true);

    if (id === "company") {
      handleChange(e);

      if (inputValue.trim() !== "") {
        setNeedValidate(false);
      }

      return;
    }

    const validateNumeric = /^\d*$/.test(inputValue);
    const validateFeet = /^[0-9'"]*$/.test(inputValue);

    if (unit === "feet" ? validateFeet : validateNumeric) {
      if (inputValue) setNeedValidate(false);
      handleChange(e);
    }
  };

  useEffect(() => {
    if (value) {
      const rule = VALIDATION_RULES;

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      const timeoutTime = 1000;

      if (id === "height") {
        const heightValue = parseInt(value, 10);
        if (
          unit === "cm" &&
          heightValue >= rule.height.min &&
          heightValue <= rule.height.max
        ) {
          handleMoveToNextInput(index, unit, inputRefs);
        }
      }

      if (unit === "feet") {
        const localFeet = value ? Number(value.replace("'", "")) : null;
        if (id === "feet" && localFeet !== null) {
          if (
            localFeet >= rule.height.feet.min &&
            localFeet <= rule.height.feet.max &&
            localFeet !== 0
          ) {
            handleMoveToNextInput(index, unit, inputRefs);
          }
        }

        const localInches = value ? Number(value.replace(`"`, "")) : null;
        if (id === "inches" && localInches !== null) {
          timeoutRef.current = window.setTimeout(() => {
            const savedFeet = feetValue
              ? Number(feetValue.replace(`"`, ""))
              : null;
            if (savedFeet !== null) {
              const totalInches = savedFeet * 12 + localInches;
              const minHeightInches =
                rule.height.feet.min * 12 + rule.height.feet.max;
              const maxHeightInches =
                rule.height.inches.min * 12 + rule.height.inches.max;
              if (
                totalInches >= minHeightInches &&
                totalInches <= maxHeightInches &&
                savedFeet !== 0
              ) {
                handleMoveToNextInput(index, unit, inputRefs);
              }
            }
          }, timeoutTime);
        }
      }

      if (id === "weight") {
        const localWeight = Number(value.split(" ")[0]);
        if (
          (unitWeight === "kg" &&
            localWeight >= rule.weight.kg.min &&
            localWeight <= rule.weight.kg.max &&
            localWeight !== 0) ||
          (unitWeight === "lbs" &&
            localWeight >= rule.weight.lbs.min &&
            localWeight <= rule.weight.lbs.max &&
            localWeight !== 0)
        ) {
          handleMoveToNextInput(index, unit, inputRefs);
        }
      }

      timeoutRef.current = window.setTimeout(() => {
        if (id === "age") {
          if (isChildren) {
            const localAgeChildren = Number(value.split(" ")[0]);
            if (
              (unitAge === "year" &&
                localAgeChildren >= 1 &&
                localAgeChildren < 18) ||
              (unitAge === "month" &&
                localAgeChildren >= 1 &&
                localAgeChildren < 24)
            ) {
              handleMoveToNextInput(index, unit, inputRefs);
            }
          } else {
            const localAge = Number(value.split(" ")[0]);
            if (
              localAge >= rule.age.min &&
              localAge <= rule.age.max &&
              localAge !== 0
            ) {
              handleMoveToNextInput(index, unit, inputRefs);
            }
          }
        }
      }, timeoutTime);
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, error]);

  useEffect(() => {
    if (errorType === id) {
      const inputElement = document.getElementById(id);

      if (inputElement) {
        const styleElement = document.createElement("style");

        const fontSize = inputStyles.fontSize;
        const baseFontSize = 16;
        const scale = parseFloat(fontSize) / baseFontSize;

        if (isMobile) {
          styleElement.innerHTML = `
          #${id}{
            border-bottom: 1px solid ${borderColor};
          }
          #${id}::placeholder {
            color: ${borderColor};
            opacity: 0.5;
            transform: scale(${scale});
            transform-origin: left;
          }
      `;
        } else {
          styleElement.innerHTML = `
          #${id}{
            border-bottom: 1px solid ${borderColor};
          }
          #${id}::placeholder {
            color: ${borderColor};
            opacity: 0.5;
          }
      `;
        }

        document.head.appendChild(styleElement);

        return () => {
          document.head.removeChild(styleElement);
        };
      }
    }
  }, [isMobile, id, errorType, borderColor, inputStyles.fontSize]);

  useEffect(() => {
    if (isMobile && isLacoste) {
      const inputs = document.querySelectorAll("input");

      inputs.forEach((input) => {
        input.addEventListener("touchstart", () => {
          input.focus({ preventScroll: true });
        });
      });
    } else {
      document.addEventListener("touchstart", (e) => {
        const target = e.target as HTMLElement;

        if (
          target &&
          (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
        ) {
          e.preventDefault();
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, brandDefined]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.stopPropagation();

      if (!disableContinue) {
        nextStep();

        const elem = document.activeElement as HTMLElement;
        elem?.blur();
      }
    }
  };

  const handleErrorIconTopPosition = () => {
    if (isGivenchy && id === "age") return "70%";
    if (isTDF && id === "height") return "35%";
    if (isTDF && id === "weight") return "35%";
    if (!isGivenchy && id === "age") return "59%";

    return "46%";
  };

  const handleErrorIconRightPosition = () => {
    if (!isGivenchy && id === "age") return "35px";
    if (isGivenchy && id === "age") return "30px";

    return "10px";
  };

  const isTextField = id === "company";

  return (
    <>
      <input
        id={id}
        type="text"
        inputMode={isTextField ? "text" : "numeric"}
        autoComplete={isTextField ? "organization" : ""}
        ref={inputRefs[index]}
        tabIndex={0}
        placeholder={isGivenchy ? "" : placeholder}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={(e) => {
          e.stopPropagation();
          handleClick && handleClick();
        }}
        style={getInputStyles({
          id,
          isMobile,
          isAndroid,
          font,
          inputStyles,
          inputStyleSelector,
        })}
      />
      {isMobile && error && !isGivenchy && !isTDF ? (
        <div
          style={{
            position: "absolute",
            right: handleErrorIconRightPosition(),
            top: handleErrorIconTopPosition(),
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        >
          <ErrorIcon style={{ color: "#DA3939" }} />
        </div>
      ) : null}
    </>
  );
};

export default TextFieldComponent;
